import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminCompliance = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    compliance_type: '',
    status: '',
    assigned_to: '',
    date_from: '',
    date_to: '',
    ...initialFilters
  });

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'overdue', label: 'Overdue' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: recordsResponse, isLoading, error } = useQuery({
    queryKey: ['adminCompliance', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/compliance${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  // Fetch statistics
  const { data: statsResponse, isLoading: isLoadingStats } = useQuery({
    queryKey: ['adminComplianceStats'],
    queryFn: async () => {
      const response = await api.get('/admin/compliance/statistics');
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/compliance', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminCompliance']);
      queryClient.invalidateQueries(['adminComplianceStats']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/compliance/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminCompliance']);
      queryClient.invalidateQueries(['adminComplianceStats']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/compliance/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminCompliance']);
      queryClient.invalidateQueries(['adminComplianceStats']);
    }
  });

  const records = useMemo(() => {
    const data = recordsResponse?.data?.data || recordsResponse?.data;
    if (data && Array.isArray(data)) {
      return data;
    }
    if (data && data.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  }, [recordsResponse]);

  const statistics = useMemo(() => {
    return statsResponse?.data || {};
  }, [statsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(record => 
      record.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.compliance_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getStatusColor = (status) => {
    const statusConfig = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return statusConfig[status] || 'bg-gray-100 text-gray-800';
  };

  return {
    records,
    isLoading: isLoading || isLoadingStats,
    error,
    filters,
    statusOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    getStatusColor,
    createRecord: createMutation.mutate,
    isCreating: createMutation.isPending,
    updateRecord: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    deleteRecord: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending
  };
};


